import { useMemo } from "react";
import { useChatRooms } from "@/hooks/use-chat-rooms";
import { useProjectChannels } from "@/hooks/use-project-channels";
import { useProjectCommentUnread } from "@/hooks/use-project-comment-reads";
import { useUsers } from "@/hooks/use-users";
import {
  buildChannelGroups,
  projectChannel,
  roomChannel,
  totalUnread,
  type Channel,
  type ChannelGroup,
} from "@/lib/chat-room/channels";

export interface ChannelMember {
  user_id: string;
  full_name: string | null;
  avatar_url: string | null;
}

export interface ChatChannels {
  channels: Channel[];
  groups: ChannelGroup[];
  totalUnread: number;
  /** Medlemmer per kanalnøkkel (avatarer i kanalhodet). */
  membersByKey: Map<string, ChannelMember[]>;
  isLoading: boolean;
}

/**
 * Kanallisten i Chat-rommet (VIB-3022): prosjektkanaler (`project_comments`,
 * VIB-3024) og husrom, grupper og direktemeldinger fra `chat_rooms`.
 * Brukeren er alltid den ekte sesjonen (`selfUser`), fordi chat-RLS og
 * `my_project_channels` autoriserer JWT-en og ikke en impersonert bruker.
 */
export function useChatChannels(selfId: string | null | undefined, enabled = true): ChatChannels {
  const rooms = useChatRooms(enabled ? selfId : null);
  const projects = useProjectChannels(selfId, enabled);
  const projectIds = useMemo(() => (projects.data ?? []).map((p) => p.project_id), [projects.data]);
  const unread = useProjectCommentUnread(projectIds, { enabled, userId: selfId ?? null });
  const { data: users } = useUsers();

  return useMemo(() => {
    const userById = new Map((users ?? []).map((u) => [u.id, u]));
    const roomList = rooms.data ?? [];
    const projectList = projects.data ?? [];

    const roomChannels = roomList.map((r) => roomChannel(r));
    const projectChannels = projectList.map((p) => projectChannel(p, unread.data?.get(p.project_id)));
    const channels = [...projectChannels, ...roomChannels];

    const membersByKey = new Map<string, ChannelMember[]>();
    roomChannels.forEach((c, i) => membersByKey.set(c.key, roomList[i].members));
    projectChannels.forEach((c, i) =>
      membersByKey.set(
        c.key,
        projectList[i].member_ids.map((id) => ({
          user_id: id,
          full_name: userById.get(id)?.full_name ?? null,
          avatar_url: userById.get(id)?.avatar_url ?? null,
        })),
      ),
    );

    return {
      channels,
      groups: buildChannelGroups(channels),
      totalUnread: totalUnread(channels),
      membersByKey,
      // Uleste for prosjektkanaler kommer i et eget kall. Kanalen må ikke
      // åpnes før det er hentet, ellers fryses «0 uleste» og kanalen markeres
      // som lest før streken og oppsummeringen har fått vite om de uleste.
      isLoading: rooms.isLoading || projects.isLoading || (projectIds.length > 0 && unread.isLoading),
    };
  }, [rooms.data, rooms.isLoading, projects.data, projects.isLoading, projectIds.length, unread.data, unread.isLoading, users]);
}
